import { i18n } from '@lingui/core';
import type { Result } from '@schibsted-nmp/search-frontend-clients/suggest';
import '@warp-ds/icons/elements/bin-16';
import { Button } from '@warp-ds/react';
import React from 'react';
import { trackResultClick } from '../suggest/tracking';
import { ResultGroup } from './ResultGroup';
import { ResultOption } from './ResultOption';
import {
    SelectAction,
    type ActionFunction,
    type ResultsProps,
} from './ResultTypes';

const REMOVE_SEARCH_HISTORY_RESULT = {
    resultType: 'LASTSEARCHES',
} as Result;

export const lastSearchResultsAction: ActionFunction = ({
    config,
    metaData,
    result,
}) => {
    if (result === REMOVE_SEARCH_HISTORY_RESULT) {
        const { apiUrl, vertical } = config;
        const deleteLastSearchesUrl = new URL(`${apiUrl}/delete/lastsearches`);
        if (vertical) {
            deleteLastSearchesUrl.searchParams.set('vertical', vertical);
        }
        window.fetch(deleteLastSearchesUrl, {
            method: 'DELETE',
            credentials: 'include',
        });
        return SelectAction.KEEP_OPEN;
    } else {
        trackResultClick(result, metaData.tracking);
        window.location.href = result.url;
        return SelectAction.CLOSE;
    }
};

export function LastSearchResults(props: ResultsProps) {
    const { headingClass, resultGroup } = props;

    const resultId = React.useId();

    const removeSearchHistoryOption = (
        <div className="mt-10">
            <ResultOption result={REMOVE_SEARCH_HISTORY_RESULT}>
                {() => (
                    <Button
                        className="flex items-center space-x-8 s-text font-bold"
                        link
                        small
                        type="button"
                    >
                        {/* @ts-expect-error Property 'w-icon-bin-16' does not exist on type 'JSX.IntrinsicElements'.ts(2339) */}
                        <w-icon-bin-16 class="flex"></w-icon-bin-16>
                        <span>
                            {i18n.t({
                                id: 'results.lastsearch.link',
                                message: 'Clear search history',
                            })}
                        </span>
                    </Button>
                )}
            </ResultOption>
        </div>
    );

    const hasLastSearches = resultGroup.results.length > 0;
    if (!hasLastSearches) {
        return null;
    }

    return (
        <ResultGroup
            heading={resultGroup.groupingHeader}
            headingClass={headingClass}
        >
            <div className="grid md:grid-cols-2 gap-x-16 gap-y-6">
                {resultGroup.results.map((result) => (
                    <ResultOption
                        className="overflow-auto"
                        linkClass="flex space-x-4"
                        key={`${result.resultType}-${result.order}-${resultId}`}
                        result={result}
                    >
                        <span className="text-s s-text-link overflow-hidden overflow-ellipsis whitespace-nowrap">
                            {result.title}
                        </span>
                        <span className="text-s s-text-subtle shrink-0">
                            <span role="presentation">
                                {
                                    ' / ' /* intentional whitespace around slash */
                                }
                            </span>
                            {result.secondaryTitle}
                        </span>
                    </ResultOption>
                ))}
            </div>
            {removeSearchHistoryOption}
        </ResultGroup>
    );
}
